import React, { useEffect, useState, useContext, useMemo } from "react";
import "./styles/ProjectReleasedText.scss";
import { getService } from "../../../services/axios/services";
import TableLoading from "../../../components/TableLoading/TableLoading";
import Breadcrumb from "../../../components/Breadcrumb/Breadcrumb";
import HeaderWithIconType from "../../../components/HeaderWithIconType/HeaderWithIconType";
import { Link, useParams } from "react-router-dom";
import ReleasedListAttachmentsController from "../ReleasedListAttachments/ReleasedListAttachmentsController";
import AppContext from "../../../context/appContext";
import ProjectReleasedTextTabs from "../ProjectReleasedTextTabs/ProjectReleasedTextTabs";
import DownloadAllSection from "../DownloadAllSection/DownloadAllSection";
import { handleSchedulerTypes } from "../../../shared/utils/handleSchedulerTypes";
import NoCommomAttachments from "../NoCommomAttachments/NoCommomAttachments";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import { useSheetAttachments } from "../../../hooks/useSheetAttachments";
import { normalizeReleasedPlanning } from "../utils/normalizeReleasedPlanning";
import ReleasedTextHeader from "../ReleasedTextHeader/ReleasedTextHeader";
import { ReleaseAddendumByContent as ReleasedAddendumByContent } from "../ReleasedAddendumByContent/ReleasedAddendumByContent";
import {
  buildAddendaList,
  buildBlockControllers,
  buildMaterialGroups,
  mapMaterialAttachmentsToReleasedList,
  mapBlockAttachmentsToReleasedList,
  getLastReleasedObservation,
} from "../../AttachmentsScheduler/PlanningGrid/mappers/attachments";
import { Box } from "@mui/material";

const ProjectReleasedText = () => {
  const routeParams = useParams();
  const [state, setState] = useState({ loading: true, list: [] });
  const [currentTab, setCurrentTab] = useState("");
  const [planningData, setPlanningData] = useState(null);
  const [loadingPlanning, setLoadingPlanning] = useState(true);

  const materialGroups = useMemo(
    () => buildMaterialGroups(planningData?.materials),
    [planningData]
  );

  const blockControllers = useMemo(
    () => buildBlockControllers(planningData?.blocks),
    [planningData]
  );

  const deduplicatedAddendaFiles = useMemo(
    () =>
      buildAddendaList({
        blocks: planningData?.blocks,
        materials: planningData?.materials,
      }),
    [planningData]
  );

  const hasAddenda = deduplicatedAddendaFiles.length > 0;

  const appContext = useContext(AppContext);
  const { downloadAndZipMultipleAttachments } = useDownloadAttachment();

  const { attachments, loadingAttachments } = useSheetAttachments({
    contentId: state.list[0]?.id,
  });

  const rawAttachments = attachments?.items ?? [];

  const otherAttachments = rawAttachments
    ?.filter((attachment) => {
      const typeName = attachment?.type?.name;
      const isReleased = attachment?.releaseManagement?.channel;

      return (
        isReleased &&
        typeName !== "Capítulo/Episódio" &&
        typeName !== "Episódio"
      );
    })
    ?.map((attachment) => ({
      ...attachment,
      comment: getLastReleasedObservation(attachment.releaseManagement),
    }));

  const getProjectReleasedText = () => {
    const filter = {
      origins: [1, 2],
      limit: 1000,
    };

    getService({
      endpoint: "textrelease",
      params: {
        ...filter,
        id: routeParams.id,
      },
    })
      .then((res) => {
        const content = res.data?.[0];

        if (!content) {
          setState({
            loading: false,
            list: [],
          });

          setLoadingPlanning(false);

          return;
        }

        setCurrentTab(handleSchedulerTypes(content.groupName));

        setState({
          loading: false,
          list: res.data,
        });

        getPlanning(content.id);
      })
      .catch(() => {
        setState({
          loading: false,
          list: [],
        });

        setLoadingPlanning(false);
      });
  };

  const getPlanning = (contentId) => {
    if (!contentId) return;

    setLoadingPlanning(true);

    getService({
      endpoint: `Planning/contents/${contentId}`,
      api: "v2",
    })
      .then((response) => {
        setPlanningData(normalizeReleasedPlanning(response));
      })
      .finally(() => {
        setLoadingPlanning(false);
      });
  };

  const chapterOrEpisode = handleSchedulerTypes(state.list[0]?.groupName);
  const isMaterial = chapterOrEpisode === "Materiais";

  function getReleasedAttachmentsFromDramaturgyLongPlanning(blocks) {
    if (!blocks?.length) return [];

    return blocks.flatMap((block) =>
      (block.chapters || []).flatMap((chapter) =>
        (chapter.attachments || []).flatMap(({ attachment, olderVersions }) => {
          const all = [attachment, ...(olderVersions || [])];

          return all.filter((a) => a?.fileId && a?.releaseManagement?.channel);
        })
      )
    );
  }

  function getReleasedAttachmentsFromPlanning(materials) {
    if (!materials?.length) return [];

    return materials.flatMap((material) =>
      (material.attachments || []).filter(
        (attachment) => attachment?.releaseManagement?.channel
      )
    );
  }

  function getReleasedAttachmentsFromChapterAndMaterials(planningData) {
    const blockAttachments = getReleasedAttachmentsFromDramaturgyLongPlanning(
      planningData?.blocks
    );

    const materialAttachments = getReleasedAttachmentsFromPlanning(
      planningData?.materials
    );

    return [...blockAttachments, ...materialAttachments];
  }

  const handleReleasedTabData = {
    "Capítulos/Sinopse": {
      message:
        "Faça download de todos os arquivos de capítulos do projeto aqui.",
      attachments: getReleasedAttachmentsFromChapterAndMaterials(planningData),
      releasedAttachments:
        getReleasedAttachmentsFromChapterAndMaterials(planningData),
    },
    Materiais: {
      message:
        "Faça download de todos os arquivos de materiais do projeto aqui.",
      attachments: getReleasedAttachmentsFromPlanning(planningData?.materials),
      releasedAttachments: getReleasedAttachmentsFromPlanning(
        planningData?.materials
      ),
    },
    Outros: {
      message:
        "Faça download de todos os arquivos do projeto aqui (exceto capítulos).",
      attachments: otherAttachments,
      releasedAttachments: otherAttachments.filter(
        (a) => a?.fileId && (a?.release ?? a?.releaseManagement?.channel)
      ),
    },
    Adendos: {
      message: "Faça download de todos os adendos liberados do projeto aqui.",
      attachments: deduplicatedAddendaFiles,
      releasedAttachments: deduplicatedAddendaFiles.filter(
        (a) => a?.fileId && a?.releaseManagement?.channel
      ),
    },
  };

  const downloadAll = () => {
    downloadAndZipMultipleAttachments({
      contentId: state.list[0]?.id,
      filesArray: handleReleasedTabData[currentTab].releasedAttachments,
      origin: "textos-liberados",
      mode: 0,
      title: state.list[0].title,
      setLoading: appContext.setShowGlassLoading,
    });
  };

  useEffect(() => {
    getProjectReleasedText();
    // eslint-disable-next-line
  }, []);

  if (state.loading || loadingAttachments || loadingPlanning) {
    return <TableLoading />;
  }

  return (
    <div className="ProjectReleasedText">
      <div className="headerProjectReleasedText">
        <Breadcrumb>
          <div className="breadcrumbContent">
            <div className="breadcrumbHeader">
              <HeaderWithIconType
                type={state.list[0]?.contentType}
                title={state.list[0]?.title}
              />

              <Link to="/textos-liberados" className="goBack">
                <span>{`« Voltar`}</span>
                <span className="goBackResponsiveText">{` para a lista de textos liberados`}</span>
              </Link>
            </div>

            <ProjectReleasedTextTabs
              contentType={state.list[0]?.contentType}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              isMaterial={isMaterial}
              hasAddenda={hasAddenda}
            />
          </div>
        </Breadcrumb>
      </div>

      <div className="container">
        {!!handleReleasedTabData[currentTab]?.attachments?.length && (
          <DownloadAllSection
            message={handleReleasedTabData[currentTab].message}
            action={downloadAll}
          />
        )}

        {currentTab === "Capítulos/Sinopse" &&
          materialGroups.map((group, index) => {
            const files = mapMaterialAttachmentsToReleasedList(group.materials);

            return (
              <Box key={`material-${group.typeId}`} sx={{ mb: "6px" }}>
                <ReleasedListAttachmentsController
                  files={files}
                  type="channels"
                  title={state.list[0]?.title}
                  contentId={state.list[0]?.id}
                  isMaterial
                  materialType={group.typeName}
                  plannedRelease={group.plannedRelease}
                  releasedAt={group.releasedAt}
                  isFirst={index === 0}
                />
              </Box>
            );
          })}

        {currentTab === "Capítulos/Sinopse" && !!blockControllers.length && (
          <ReleasedTextHeader />
        )}

        {currentTab === "Capítulos/Sinopse" &&
          blockControllers.map((block) => {
            const files = mapBlockAttachmentsToReleasedList([block]);

            return (
              <Box key={block.id} sx={{ mb: "6px" }}>
                <ReleasedListAttachmentsController
                  files={files}
                  type="channels"
                  title={state.list[0]?.title}
                  contentId={state.list[0]?.id}
                  isBlockMode
                  blockNumber={block.number}
                  chaptersCount={block.chapters?.length ?? 0}
                  plannedRelease={block.planning?.release}
                  releasedAt={block.realized?.channelRelease}
                />
              </Box>
            );
          })}

        {currentTab === "Materiais" &&
          materialGroups.map((group, index) => {
            const files = mapMaterialAttachmentsToReleasedList(group.materials);

            const materialType =
              group.typeName === "Capítulo/Episódio"
                ? "Episódios"
                : group.typeName;

            return (
              <Box key={`material-${group.typeId}`} sx={{ mb: "6px" }}>
                <ReleasedListAttachmentsController
                  files={files}
                  type="channels"
                  title={state.list[0]?.title}
                  contentId={state.list[0]?.id}
                  isMaterial
                  materialType={materialType}
                  plannedRelease={group.plannedRelease}
                  releasedAt={group.releasedAt}
                  isFirst={index === 0}
                  renameEpisodeLabel
                />
              </Box>
            );
          })}

        {currentTab === "Adendos" && (
          <ReleasedAddendumByContent
            files={deduplicatedAddendaFiles}
            title={state.list[0]?.title}
            contentId={state.list[0]?.id}
            isChapter={!isMaterial}
          />
        )}

        {!!otherAttachments?.length && currentTab === "Outros" && (
          <ReleasedListAttachmentsController
            files={otherAttachments}
            title={state.list[0]?.title}
            contentId={state.list[0].id}
            type="channels"
          />
        )}

        {!otherAttachments?.length && currentTab === "Outros" && (
          <NoCommomAttachments />
        )}
      </div>
    </div>
  );
};

export default ProjectReleasedText;
